import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { useSetUpMap } from "../../../../App/useSetUpMap";
import { MapContext } from '../../../../App/MapContextContainer';
import PopupTemplate from '@arcgis/core/PopupTemplate';
import MapContainer from '../../../../components/Maps/MapContainer';
import { useNotificationsLayer } from '../../../../App/useNotificationsLayer';
import { IClashToForget, ObservationsContext } from '../../ObservationsContextContainer';
import useSetObservationsClustering from './useSetObservationsClusterPopupContent';
import { useGetObservationPopupLine } from './useGetObservationPopupLine';
import { getFromAndToDatesFromMonths, makeCuttingRealizationPracticePropQueryable, storageAvailable } from '../../../utils';
import Graphic from '@arcgis/core/Graphic';
import Point from '@arcgis/core/geometry/Point';
import { PREFERRED_WKID } from '../../../CustomAreas/UserDataContextContainer';
import { whenFalse } from '@arcgis/core/core/watchUtils';
import Polygon from '@arcgis/core/geometry/Polygon';
import Legend from '@arcgis/core/widgets/Legend';
import Expand from '@arcgis/core/widgets/Expand';
import useGridRenderer from '../../../../components/Maps/gridRenderer';
import { PageContext } from '../../../../App/PageContextContainer';
import errors from '../../../../App/errors';
import loadingNotifications from '../../../../App/loadingNotifications';
import { createRadiusGraphicForPoint, getClashToForgetFromFeature } from './utils';
import ActionButton from "@arcgis/core/support/actions/ActionButton.js";
import * as reactiveUtils from "@arcgis/core/core/reactiveUtils.js";
import useOpenAreasLayer from '../../../../components/Maps/useOpenAreasLayer';
import useObservationClashLayers from './useObservationClashLayers';
import useShowAllLajiFiObservationsLayer from './useShowAllLajiFiObservationsLayer';
import { useTranslation } from 'react-i18next';

export default function ObservationsMap() {
    const elementRef = useRef(null);
    const { webmap, view } = useSetUpMap(elementRef, "6e34c9c7b08d41779b23bf1241649275");
    const mapContext = useContext(MapContext);
    useNotificationsLayer(webmap, view, new Date(2020, 3, 1));
    const observationsContext = useContext(ObservationsContext);
    const pointsLayerReady = useRef(false);
    const { getObservationLine } = useGetObservationPopupLine();
    const [fetchedMapPages, setFetchedMapPages] = useState<string[]>([]);
    const { grid25kmGeojsonLayer, visibleGrid25kmGeojsonLayer } = useGridRenderer(
        [0, 10, 25, 50, 100, 150],
        "Metsänkäyttöilmoitukset lajihavainnoissa per 25km karttalehti");
    const pageContext = useContext(PageContext);
    const viewWatcherRef = useRef<IHandle>();
    const openAreasLayer = useOpenAreasLayer(view);
    const { pointsObservationsFeatureLayer, polygonsObservationsFeatureLayer } = useObservationClashLayers();
    const { allLajiFiObservationsLayer } = useShowAllLajiFiObservationsLayer(view);
    const { t, i18n } = useTranslation("observations");

    // Add this action to the popup so it is always available in this view
    const hideClashAction = useMemo(() => new ActionButton({
        title: t("popupinToiminto"),
        id: "forget-observation",
        type: "button",
        image: "",
        className: "hide-esri-default-action-icon"
    }), [t, i18n.language]);

    useEffect(() => {
        if (pointsObservationsFeatureLayer) {
            pointsObservationsFeatureLayer.title = t("mkiHavainnoissaPisteetKerrosOtsikko");
        }

        if (polygonsObservationsFeatureLayer) {
            polygonsObservationsFeatureLayer.title = t("mkiHavainnoissaAlueetKerrosOtsikko");
        }

        if (visibleGrid25kmGeojsonLayer) {
            visibleGrid25kmGeojsonLayer.title = t("mkiHavainnoissaPerKarttaruutuKerrosOtsikko");
        }
    }, [i18n.language, t]);

    useEffect(() => {
        if (observationsContext.observationsState.allCollections && observationsContext.observationsState.allTaxons) {
            const popupTemplate = new PopupTemplate({
                title: t("popupinOtsikko"),
                content: getObservationLine,
                actions: storageAvailable("localStorage") ? [hideClashAction] : []
            });

            pointsObservationsFeatureLayer.popupTemplate = popupTemplate;
            polygonsObservationsFeatureLayer.popupTemplate = popupTemplate;
        }
    }, [pointsLayerReady.current, observationsContext.observationsState.allCollections, observationsContext.observationsState.allTaxons, i18n.language, t]);

    useSetObservationsClustering(view, pointsObservationsFeatureLayer, pointsLayerReady);

    useEffect(() => {
        const asyncCall = async () => {
            if (mapContext.mapAndViewReady && observationsContext.observationsState.isInitialized) {
                try {
                    pageContext.setLoadingSomething(loadingNotifications.loadingObservationsGrid);

                    let crpQuery = "";
                    let taxonQuery = "";
                    let collectionQuery = "";
                    let startDateQuery = "";
                    let endDateQuery = "";
                    let declarationFromDateQuery = "";
                    let declarationToDateQuery = "";

                    if (observationsContext.observationsState.cuttingRealizationPractices.length > 0) {
                        crpQuery = observationsContext.observationsState.cuttingRealizationPractices.reduce((prev, curr) => {
                            return (prev ? prev + "." + curr : curr);
                        }, "");
                    }

                    if (observationsContext.observationsState.collectionIds.length > 0) {
                        collectionQuery = observationsContext.observationsState.collectionIds.reduce((prev, curr) => {
                            const currSplit = curr.split(".");
                            const valueToUse = currSplit[2];

                            return (prev ? prev + "." + valueToUse : valueToUse);
                        }, "");
                    }

                    if (observationsContext.observationsState.taxonIds.length > 0) {
                        taxonQuery = observationsContext.observationsState.taxonIds.reduce((prev, curr) => {
                            const currSplit = curr.split(".");
                            const valueToUse = currSplit[2];

                            return (prev ? prev + "." + valueToUse : valueToUse);
                        }, "");
                    }

                    if (observationsContext.observationsState.observationMonths.length > 0) {
                        const { fromDate, toDate } = getFromAndToDatesFromMonths(observationsContext.observationsState.observationMonths);

                        if (fromDate && toDate) {
                            startDateQuery = `${fromDate.getFullYear()}-${(fromDate.getMonth() + 1 + "").padStart(2, "0")}-01`;
                            endDateQuery = `${toDate.getFullYear()}-${(toDate.getMonth() + 1 + "").padStart(2, "0")}-${toDate.getDate()}`;
                        }
                    }

                    if (observationsContext.observationsState.declarationArrivalMonths.length > 0) {
                        const { fromDate, toDate } = getFromAndToDatesFromMonths(observationsContext.observationsState.declarationArrivalMonths);

                        if (fromDate && toDate) {
                            declarationFromDateQuery = `${fromDate.getFullYear()}-${(fromDate.getMonth() + 1 + "").padStart(2, "0")}-01`;
                            declarationToDateQuery = `${toDate.getFullYear()}-${(toDate.getMonth() + 1 + "").padStart(2, "0")}-${toDate.getDate()}`;
                        }
                    }

                    const unitIdsToLeaveOut: string[] = observationsContext.observationsState.clashesToForget ? observationsContext.observationsState.clashesToForget.map(c => c ? c.id : "") : [];

                    const responseGrid = await fetch(`${process.env.REACT_APP_API_URL}/api/GetObservations25KmGrid?crp=${crpQuery}&taxons=${taxonQuery}` +
                        `&collections=${collectionQuery}&startDate=${startDateQuery}&endDate=${endDateQuery}` +
                        `&declarationArrivalDateFrom=${declarationFromDateQuery}&declarationArrivalDateTo=${declarationToDateQuery}`, {
                        body: JSON.stringify(unitIdsToLeaveOut), method: "POST"
                    });

                    const gridData = await responseGrid.json();

                    const bufferedGraphics: Graphic[] = [];

                    for (const feature of gridData.mapPages.features) {
                        const g = new Graphic({
                            attributes: feature.properties,
                        });

                        const p: Polygon = new Polygon({
                            rings: feature.geometry.coordinates[0],
                            spatialReference: { wkid: PREFERRED_WKID },
                            hasZ: false
                        });

                        g.geometry = p;

                        bufferedGraphics.push(g);
                    }

                    const queryArea = grid25kmGeojsonLayer.createQuery();
                    const response = await grid25kmGeojsonLayer.queryFeatures(queryArea);

                    const addEdits = {
                        deleteFeatures: response.features,
                        addFeatures: bufferedGraphics
                    };

                    await grid25kmGeojsonLayer.applyEdits(addEdits);

                    await visibleGrid25kmGeojsonLayer.applyEdits(addEdits);
                } catch (error) {
                    pageContext.setMapError(errors.loadingObservationsGridFailed);
                }
                finally {
                    pageContext.resetLoadingSomething(loadingNotifications.loadingObservationsGrid);
                }
            }
        };

        asyncCall();
    }, [mapContext.mapAndViewReady, observationsContext.observationsState.cuttingRealizationPractices,
    observationsContext.observationsState.collectionIds, observationsContext.observationsState.taxonIds,
    observationsContext.observationsState.observationMonths, observationsContext.observationsState.declarationArrivalMonths,
    observationsContext.observationsState.isInitialized]);

    const forgetAllVisibleClashesHandler = useCallback(async () => {
        const pointsQuery = pointsObservationsFeatureLayer.createQuery();
        pointsQuery.geometry = view.extent;

        const pointFeatures = await pointsObservationsFeatureLayer.queryFeatures(pointsQuery);

        const polygonsQuery = pointsObservationsFeatureLayer.createQuery();
        polygonsQuery.geometry = view.extent;

        const polygonFeatures = await polygonsObservationsFeatureLayer.queryFeatures(polygonsQuery);

        const combinedFeatures = [...pointFeatures.features, ...polygonFeatures.features];

        if (combinedFeatures.length > 0) {
            const clashesToForget: IClashToForget[] = [];

            for (const feature of combinedFeatures.filter(f => f && f.attributes && f.attributes["id"])) {
                clashesToForget.push(getClashToForgetFromFeature(feature));
            }

            observationsContext.observationsStateDispatch({ type: "setClashesToForget", clashes: clashesToForget });
        }
    }, []);

    const legendExpand = useMemo(() => {
        let legend = new Legend({
            view: view,
            basemapLegendVisible: false,
            label: "Määrä"
        });

        return new Expand({
            expandIconClass: "esri-icon-legend",
            view: view,
            expanded: false,
            content: legend
        });
    }, [view]);

    useEffect(() => {
        if (viewWatcherRef.current) {
            viewWatcherRef.current.remove();
        }

        viewWatcherRef.current = view.watch("zoom", () => {
            if (view.zoom > 11) {
                legendExpand.visible = false;
            }
            else {
                legendExpand.visible = true;
            }
        });

        return () => viewWatcherRef.current?.remove();
    }, [legendExpand, view]);

    useEffect(() => {
        const asyncCall = async () => {
            if (mapContext.mapAndViewReady) {
                view.popup.viewModel.includeDefaultActions = false;

                observationsContext.observationsStateDispatch({
                    type: "setForgetAllVisibleClashesHandler",
                    handler: forgetAllVisibleClashesHandler
                });

                reactiveUtils.on(
                    () => view.popup,
                    "trigger-action",
                    (event) => {
                        if (event.action.id === "forget-observation") {
                            const feature = view.popup.selectedFeature;

                            if (feature.attributes["id"]) {
                                const clashToForget = getClashToForgetFromFeature(feature);

                                observationsContext.observationsStateDispatch({ type: "setClashesToForget", clashes: [clashToForget] });
                            }
                        }
                    });

                view.ui.add(legendExpand, "bottom-left");

                webmap.add(grid25kmGeojsonLayer);
                webmap.add(visibleGrid25kmGeojsonLayer);
                webmap.add(openAreasLayer);
                webmap.add(allLajiFiObservationsLayer);
                webmap.add(pointsObservationsFeatureLayer);
                webmap.add(polygonsObservationsFeatureLayer);

                view.whenLayerView(grid25kmGeojsonLayer).then((lView) => {
                    whenFalse(lView, "updating", async () => {
                        const queryArea = grid25kmGeojsonLayer.createQuery();

                        queryArea.geometry = view.extent;

                        const response = await grid25kmGeojsonLayer.queryFeatures(queryArea);

                        if (view.zoom >= 10) {
                            let mapPagesConcatenated = "";

                            response.features.forEach(f => {
                                if (fetchedMapPages.indexOf(f.attributes.LEHTITUNNU) < 0) {
                                    fetchedMapPages.push(f.attributes.LEHTITUNNU);

                                    mapPagesConcatenated = mapPagesConcatenated + "," + f.attributes.LEHTITUNNU;
                                }
                            });

                            if (mapPagesConcatenated.length > 0) {
                                try {
                                    pageContext.setLoadingSomething(loadingNotifications.loadingObservationClashes);

                                    const response = await fetch(`${process.env.REACT_APP_API_URL}/api/GetObservationClashes/${mapPagesConcatenated}`);
                                    const clashData = await response.json();
                                    const bufferedGraphics: Graphic[] = [];

                                    for (const feature of clashData) {
                                        const modifiedFeature = makeCuttingRealizationPracticePropQueryable(feature);

                                        const clashGraphic = new Graphic({
                                            attributes: { ...modifiedFeature.properties, isRadius: 0 }
                                        });

                                        if (observationsContext.observationsState.clashesToForget &&
                                            feature.properties["id"] && // NOTE: The older clashes don't have the id at all
                                            observationsContext.observationsState.clashesToForget.findIndex(c => c && c.id === feature.properties["id"]) >= 0) {
                                            // No-op, this is ignored so we don't put this on the map.
                                        }
                                        else if (feature.geometry.type === "Point") {
                                            const clashPointGeometry: Point = new Point({
                                                latitude: feature.geometry.coordinates[1],
                                                longitude: feature.geometry.coordinates[0],
                                                spatialReference: { wkid: PREFERRED_WKID }
                                            });

                                            clashGraphic.geometry = clashPointGeometry;

                                            if (feature.properties["gathering.interpretations.coordinateAccuracy"] > 1) {
                                                bufferedGraphics.push(createRadiusGraphicForPoint(
                                                    clashPointGeometry,
                                                    feature.properties["gathering.interpretations.coordinateAccuracy"],
                                                    modifiedFeature.properties));
                                            }
                                            else if (feature.properties["clashRadius"] && feature.properties["clashRadius"] > 1) {
                                                bufferedGraphics.push(createRadiusGraphicForPoint(
                                                    clashPointGeometry,
                                                    feature.properties["clashRadius"],
                                                    modifiedFeature.properties));
                                            }
                                            else {
                                                bufferedGraphics.push(clashGraphic);
                                            }
                                        }
                                        else if (feature.geometry.type === "Polygon") {
                                            const p: Polygon = new Polygon({
                                                rings: feature.geometry.coordinates,
                                                spatialReference: { wkid: PREFERRED_WKID }
                                            });

                                            clashGraphic.geometry = p;

                                            bufferedGraphics.push(clashGraphic);
                                        }
                                    }

                                    const addEdits = {
                                        addFeatures: bufferedGraphics.filter(g => g.geometry.type === "point")
                                    };

                                    await pointsObservationsFeatureLayer.applyEdits(addEdits);

                                    addEdits.addFeatures = bufferedGraphics.filter(g => g.geometry.type === "polygon");

                                    await polygonsObservationsFeatureLayer.applyEdits(addEdits);
                                } catch (error) {
                                    pageContext.setMapError(errors.loadingObservationsForMapPageFailed);
                                }
                                finally {
                                    pageContext.resetLoadingSomething(loadingNotifications.loadingObservationClashes);
                                }
                            }
                        }
                    });
                });

                pointsLayerReady.current = true;
            }
        }

        asyncCall();
    }, [mapContext.mapAndViewReady, webmap]);

    return <MapContainer elementRef={elementRef} view={view}></MapContainer>
}
